import React, { useState, MouseEvent } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Popover, Theme } from '@mui/material';
import { IconButton, IIconButton } from '@components/iconButton';
import { CardLogout } from '@components/logout/CardLogout';
import { EditRolesModal } from '@components/logout/EditRolesModal';
import { FeatureToggleModal } from '@components/debugModeControls/FeatureToggleModal';

import { useBrokerageById } from '@services/user';
import { UserClaimsResponse } from '@models/user';
import { useAlertStore } from '@store/AlertStore';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useOktaAuth } from '@okta/okta-react';
import { useGetScreenSize } from '@utils/useGetScreenSize';

interface ILogout {
  iconButtonProps?: IIconButton;
}

export const clearUserStorageItems = () => {
  // Clear table store items from localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('table-store')) {
      localStorage.removeItem(key);
    }
  });

  // Clear email confirmation modal dismissed keys from sessionStorage
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('email-confirmation-modal-dismissed-')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
};

export const ProfileBadge = (props: ILogout) => {
  const headerHeight = useAlertStore((state) => state.headerHeight);
  const { isMobileScreen } = useGetScreenSize();
  const { oktaAuth } = useOktaAuth();
  const [{ isInternalUser }, currentUser] = useStore((state) => [state.userAuth, state.currentUser], shallow);
  const { userBrokerage } = useBrokerageById(
    !isInternalUser && currentUser?.brokerageUsers[0]?.brokerageId !== undefined,
    currentUser?.brokerageUsers[0]?.brokerageId,
  );

  const { iconButtonProps } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showEditRolesModal, setShowEditRolesModal] = useState(false);
  const [showFeatureToggleModal, setShowFeatureToggleModal] = useState(false);
  const { firstName, lastName, email } = currentUser || ({} as UserClaimsResponse);

  const handleOpenPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);

  const handleLogout = async (event: MouseEvent<HTMLButtonElement>) => {
    clearUserStorageItems();
    event.preventDefault();
    setAnchorEl(null);
    datadogRum.stopSessionReplayRecording();

    if (oktaAuth.signOut) {
      await oktaAuth.signOut();
    }
  };

  const handleOpenEditRoles = () => {
    // Close popover first to avoid stacked focus, then open modal
    handleClosePopover();
    setTimeout(() => setShowEditRolesModal(true), 0);
  };

  const handleOpenFeatureToggles = () => {
    handleClosePopover();
    setTimeout(() => setShowFeatureToggleModal(true), 0);
  };

  return (
    <>
      <IconButton
        id='logout-button'
        aria-controls={openPopover ? 'logout-popover' : undefined}
        aria-expanded={openPopover ? 'true' : undefined}
        aria-haspopup='true'
        aria-label='profileMenuButton'
        aria-valuetext='Profile Menu'
        onClick={handleOpenPopover}
        variant='circularDark'
        {...iconButtonProps}
      />
      <Popover
        id='logout-popover'
        aria-labelledby='profileMenuButton'
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: headerHeight - 8,
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: headerHeight,
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            transform: isMobileScreen ? 'unset' : 'translateX(-4px)',
          },
        }}
        sx={{
          '& .MuiPaper-elevation8': {
            // primary.dark is a light slate on dark page palettes; match the notification panel
            backgroundColor: (theme: Theme) => (theme.palette.theme === 'lio' ? 'primary.dark' : 'background.main'),
            padding: 0,
            borderRadius: 'shape.borderRadius',
            margin: { xs: 0, sm: '0 0 0 44px' },
            width: { xs: '100vw', sm: 'auto' },
            marginTop: -1,
          },
        }}
      >
        <CardLogout
          email={email}
          firstName={firstName}
          lastName={lastName}
          brokerage={
            currentUser?.brokerageUsers && userBrokerage
              ? {
                  name: userBrokerage.name,
                  multiple: currentUser.brokerageUsers?.length > 1,
                }
              : undefined
          }
          onLogout={handleLogout}
          onUserCircleClick={handleClosePopover}
          onOpenEditRoles={handleOpenEditRoles}
          onOpenFeatureToggles={handleOpenFeatureToggles}
        />
      </Popover>
      {showEditRolesModal ? <EditRolesModal open={true} onClose={() => setShowEditRolesModal(false)} /> : null}
      {showFeatureToggleModal ? (
        <FeatureToggleModal open={true} onClose={() => setShowFeatureToggleModal(false)} />
      ) : null}
    </>
  );
};
